import { useCallback, useEffect, useState } from "react";
import { yerelGunBasi } from "@koclukpanel/contracts";
import { Buton, cn, Iskelet, Kart } from "@koclukpanel/ui";
import { ApiHatasi, icsBaglantim, icsYenile, takvimim, type TakvimEtkinligi } from "./api";
import { YaklasanGorusmelerim } from "./YaklasanGorusmeler";

/**
 * OGRENCI TAKVIMI VE ICS ABONELIGI — F-CAL
 *
 * Ogrenci kendi takvimini goruyor ve telefonunun takvim uygulamasina
 * abone edebiliyor.
 */

/**
 * Onumuzdeki yedi gun.
 *
 * ================================================================
 * BASLANGIC YEREL GUNDEN — UTC BILESENI BIR GUN GERIDEN GELIYORDU
 * ================================================================
 * `simdi.getUTCDate()` Turkiye'de gece 00:00-03:00 arasi hala DUNKU
 * gunu veriyor (UTC+3). Yani gece yarisindan sonra takvimi acan
 * ogrenci icin aralik dunden basliyor ve "onumuzdeki yedi gun" aslinda
 * "dunden itibaren yedi gun" oluyordu — son gun eksik kaliyordu.
 *
 * Ayni kok sebep `HaftalikProgram` seridinde butun haftayi bir hafta
 * geri atiyordu; kural artik sozlesmede tek kaynakta.
 */
function haftaAraligi(): { baslangic: string; bitis: string } {
  const bas = yerelGunBasi();
  return {
    baslangic: bas.toISOString(),
    bitis: new Date(bas.getTime() + 7 * 24 * 3600_000).toISOString(),
  };
}

function saat(iso: string): string {
  return new Date(iso).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
}

/**
 * Yerel takvim gununun sira numarasi.
 *
 * Iki tarihin "kac gun ara" oldugunu ms farkiyla olcmek yaniltici:
 * 23:50 ile ertesi sabah 00:10 arasi 20 dakika ama BASKA gunler.
 * Yerel gun parcalarini alip UTC'ye cevirerek tam gun sinirinda
 * karsilastiriliyor.
 */
function gunSirasi(d: Date): number {
  return Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / 86_400_000;
}

/**
 * ================================================================
 * GÜN ADI SATIRIN İÇİNDE, AYRI BİR BAŞLIKTA DEĞİL
 * ================================================================
 * Önceki hâl günleri `<section>` başlıklarıyla grupluyordu
 * ("pazartesi, 8 eylül"). Çizim (`OgrenciTakvimMasaustu.dc.html`)
 * bunun yerine her satırın soluna dar bir sütun koyuyor: üstte gün,
 * altında saat.
 *
 * Sebep listenin kendisinde: öğrencinin haftada dört beş şeyi var.
 * Beş öğe için altı başlık çizmek, içeriğin kendisinden çok başlık
 * göstermek demekti — ve boş günler zaten listelenmiyor, yani
 * başlıklar arasındaki boşluk da bilgi taşımıyordu.
 *
 * "Bugün" ve "Yarın" ad olarak yazılıyor: öğrenci tarihi değil
 * uzaklığı arıyor. Üçüncü günden sonra gün adı yetiyor, çünkü aralık
 * zaten yedi gün — "Salı" tek bir salıya işaret ediyor.
 */
function gunEtiketi(iso: string, bugun: Date): string {
  const g = new Date(iso);
  const fark = gunSirasi(g) - gunSirasi(bugun);
  if (fark === 0) return "Bugün";
  if (fark === 1) return "Yarın";
  return g.toLocaleDateString("tr-TR", { weekday: "long" });
}

export function Takvimim() {
  const [etkinlikler, setEtkinlikler] = useState<TakvimEtkinligi[]>([]);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [hata, setHata] = useState<string | null>(null);

  const yukle = useCallback(async () => {
    setYukleniyor(true);
    setHata(null);
    try {
      const a = haftaAraligi();
      const c = await takvimim(a.baslangic, a.bitis);
      setEtkinlikler(c.etkinlikler);
    } catch (err) {
      setHata(err instanceof ApiHatasi ? err.message : "Takvim alınamadı.");
    } finally {
      setYukleniyor(false);
    }
  }, []);

  useEffect(() => {
    void yukle();
  }, [yukle]);

  const bugun = new Date();
  // Sunucu sirali donuyor; yine de savunma amacli siralaniyor —
  // sirasiz bir liste bu ekranda "sıradaki en üstte" sozunu bozardi.
  const sirali = [...etkinlikler].sort((a, b) => a.startsAt.localeCompare(b.startsAt));

  return (
    <div className="flex flex-col gap-4 p-4">
      <div>
        {/* h2: Takvim, Program sekmesinin (h1) ikinci gorunumu. */}
        <h2 className="text-notr-900 text-lg font-semibold">Takvimim</h2>
        {/*
          ALT BAŞLIK ÇİZİMDEN. Boş günlerin listelenmediğini SÖYLEMEK
          gerekiyor: söylenmezse öğrenci araya giren boşluğu eksik veri
          sanar ve "çarşamba nerede" diye sorar. Cevap "çarşamba boş".
        */}
        <p className="text-notr-600 mt-1 text-[13.5px] leading-relaxed">
          Yalnızca senin için planlanmış olanlar — boş günler listelenmiyor.
        </p>
      </div>

      {/* F-SES-02b — "Görüşmeye katıl" düğmesi; yaklaşan görüşme yoksa hiç çizilmez */}
      <YaklasanGorusmelerim />

      {yukleniyor && etkinlikler.length === 0 && <Iskelet satir={4} />}

      {hata && (
        <div className="flex flex-col gap-2">
          <p role="alert" className="text-hata-500 text-sm">
            {hata}
          </p>
          <Buton gorunum="ikincil" onClick={() => void yukle()}>
            Tekrar dene
          </Buton>
        </div>
      )}

      {!yukleniyor && !hata && etkinlikler.length === 0 && (
        <p className="text-notr-600 text-sm">Önümüzdeki yedi günde planlanmış bir şey yok.</p>
      )}

      <ul className="flex flex-col gap-2">
        {sirali.map((e) => (
          <li
            key={e.id}
            className="min-h-dokunma rounded-alan border-notr-200 bg-notr-0 shadow-kart flex items-center gap-4 border px-[17px] py-[15px]"
          >
            {/*
              DAR SÜTUN: üstte gün, altında saat. Çizimde 74px; burada
              `w-[68px]` çünkü ekran 390px ve başlıktan sonra kalan
              genişliğin tamamı içeriğin.
            */}
            <span className="flex w-[68px] shrink-0 flex-col items-center gap-px">
              <span className="text-notr-900 text-[13px] font-semibold">
                {gunEtiketi(e.startsAt, bugun)}
              </span>
              <span className="text-notr-600 text-[12.5px] tabular-nums">
                {e.isAllDay ? "Tüm gün" : saat(e.startsAt)}
              </span>
            </span>
            <span className="min-w-0 flex-1">
              {/*
                Kendi takviminde her zaman AYRINTI gelir; yine de
                daraltma yapiliyor cunku tip ayrik birlesim ve
                `title`a daraltmadan ulasilamiyor. Bir gun bu
                ekran baskasinin takvimini gostermeye baslarsa
                derleyici burayi hatirlatir.
              */}
              {e.mod === "AYRINTI" ? (
                <>
                  <span className="text-notr-900 block truncate text-sm font-medium">
                    {e.title}
                  </span>
                  {e.location && (
                    <span className="text-notr-600 block truncate text-xs">{e.location}</span>
                  )}
                </>
              ) : (
                <span className="text-notr-600 block text-sm">Meşgul</span>
              )}
            </span>
          </li>
        ))}
      </ul>

      {/*
        ================================================================
        "NEDEN AYLIK IZGARA DEĞİL?" — ÇİZİMİN KENDİ CÜMLESİ
        ================================================================
        Öğrenci takvim deyince ay ızgarası bekliyor ve bulamayınca
        özelliğin eksik olduğunu düşünüyor. Sebebi yazmak, eksikliği
        karara çeviriyor: ayda dört beş şeyi olan birine otuz kutu
        göstermek, yirmi beş boş kutu göstermek demek.
      */}
      {sirali.length > 0 && (
        <Kart className="flex flex-col gap-2">
          <h2 className="text-notr-900 text-[15px] font-semibold">Neden aylık ızgara değil?</h2>
          <p className="text-notr-700 text-[13.5px] leading-relaxed">
            Ayda dört ya da beş şeyin var. Otuz kutuluk bir ızgara bunları göstermek yerine{" "}
            <strong className="text-notr-900">yirmi beş boş kutu</strong> gösterirdi.
          </p>
          <p className="text-notr-700 text-[13.5px] leading-relaxed">
            Liste, sıradakini en üste koyar. Aradığın şey neredeyse her zaman sıradaki.
          </p>
        </Kart>
      )}

      <IcsBolumu />
    </div>
  );
}

/**
 * ================================================================
 * ICS ADRESI SIFRE GIBI DAVRANILIYOR
 * ================================================================
 * Jetonun KENDISI kimlik: adresi bilen herkes ogrencinin takvimini
 * okuyabiliyor, oturum acmasi gerekmiyor. Bu yuzden ekran:
 *
 *   - adresi VARSAYILAN OLARAK GIZLIYOR (omuz ustunden bakan biri
 *     ekranda duran adresi fotograflayabilir)
 *   - yenilemenin eski adresi OLDURDUGUNU onceden soyluyor ve onay
 *     istiyor — kullanici yenileyip telefonundaki aboneligin neden
 *     bozuldugunu sonradan anlamamali
 */
// Uzunluk sabit: gercek adresin uzunlugu bile disari sizmasin.
const GIZLI_ADRES = "•".repeat(32);

function IcsBolumu() {
  const [adres, setAdres] = useState<string | null>(null);
  const [aktif, setAktif] = useState(false);
  const [gorunur, setGorunur] = useState(false);
  const [onayIstiyor, setOnayIstiyor] = useState(false);
  const [kopyalandi, setKopyalandi] = useState(false);
  const [hata, setHata] = useState<string | null>(null);
  const [islemde, setIslemde] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const c = await icsBaglantim();
        setAdres(c.adres);
        setAktif(c.aktif);
      } catch (err) {
        setHata(err instanceof ApiHatasi ? err.message : "Abonelik adresi alınamadı.");
      }
    })();
  }, []);

  const yenile = async () => {
    setIslemde(true);
    setHata(null);
    try {
      const c = await icsYenile();
      setAdres(c.adres);
      setAktif(true);
      setOnayIstiyor(false);
      setGorunur(false);
      setKopyalandi(false);
    } catch (err) {
      setHata(err instanceof ApiHatasi ? err.message : "Adres yenilenemedi.");
    } finally {
      setIslemde(false);
    }
  };

  const kopyala = async () => {
    if (!adres) return;
    try {
      await navigator.clipboard.writeText(adres);
      setKopyalandi(true);
    } catch {
      // Pano izni yoksa adresi gostermek tek care — sessiz kalmak degil.
      setGorunur(true);
      setHata("Kopyalanamadı; adresi elle seçip kopyalayabilirsin.");
    }
  };

  return (
    <Kart className="mt-2 flex flex-col gap-3">
      <h2 className="text-notr-900 text-sm font-semibold">Telefonunun takvimine ekle</h2>
      <p className="text-notr-600 text-sm">
        Bu adresi telefonunun takvim uygulamasına eklersen programın orada da görünür ve
        kendiliğinden güncellenir.
      </p>

      {hata && (
        <p role="alert" className="text-hata-500 text-sm">
          {hata}
        </p>
      )}

      {adres && (
        <>
          <div className="flex flex-col gap-2">
            {/*
              GIZLIYKEN ADRES DOM'DA YOK — UI/UX Dalga 1 (axe G4a).
              Eskiden adres soluk (notr-400) ve bulanik ciziliyordu: kontrast
              denetimi bunu okunmasi gereken metin sayip dusuruyordu, ustelik
              adres DOM'da duruyordu (sayfa kaynagi, ekran goruntusu araci,
              blur'u kapatan bir eklenti onu okuyabilirdi). Yer tutucu nokta
              hem okunur kontrastta hem de jetondan hicbir sey tasimiyor.
            */}
            <code
              className={cn(
                "rounded-alan bg-notr-50 block overflow-x-auto p-2 text-xs",
                gorunur ? "text-notr-800" : "text-notr-600 select-none",
              )}
              aria-hidden={!gorunur}
            >
              {gorunur ? adres : GIZLI_ADRES}
            </code>

            <div className="flex flex-wrap gap-2">
              <Buton gorunum="ikincil" onClick={() => setGorunur((g) => !g)}>
                {gorunur ? "Gizle" : "Göster"}
              </Buton>
              <Buton onClick={() => void kopyala()}>{kopyalandi ? "Kopyalandı" : "Kopyala"}</Buton>
            </div>
          </div>

          <p className="text-uyari-600 text-xs">
            Bu adres <strong>şifren gibidir</strong>: adresi bilen herkes takvimini görebilir.
            Kimseyle paylaşma.
          </p>

          {!aktif && (
            <p className="text-notr-600 text-xs">
              Abonelik şu an kapalı; adresi kullanmaya başladığında açılır.
            </p>
          )}

          {/*
            YENILEME ONAY ISTIYOR.
            Tek tikla yenilenseydi ogrenci merakla basar, telefonundaki
            abonelik sessizce olur ve haftalar sonra "takvimim
            guncellenmiyor" diye fark ederdi.
          */}
          {!onayIstiyor ? (
            <Buton gorunum="sessiz" onClick={() => setOnayIstiyor(true)}>
              Adresi yenile
            </Buton>
          ) : (
            <div className="rounded-alan bg-uyari-500/10 flex flex-col gap-2 p-3">
              <p className="text-notr-800 text-sm">
                Yeni bir adres oluşturulacak ve{" "}
                <strong>eski adres hemen çalışmayı bırakacak</strong>. Telefonundaki aboneliği yeni
                adresle baştan eklemen gerekir.
              </p>
              <div className="flex gap-2">
                <Buton onClick={() => void yenile()} disabled={islemde}>
                  {islemde ? "Yenileniyor…" : "Evet, yenile"}
                </Buton>
                <Buton gorunum="sessiz" onClick={() => setOnayIstiyor(false)}>
                  Vazgeç
                </Buton>
              </div>
            </div>
          )}
        </>
      )}
    </Kart>
  );
}
